import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { authApi } from "@/lib/auth";
import { Card, Notice } from "@/components/ui";

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "10px 12px 10px 38px",
  fontSize: 14,
  color: "var(--wf-ink)",
  fontFamily: "var(--wf-font-body)",
  outline: "none",
  width: "100%",
};

function Field({
  label,
  icon: IconCmp,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  children: (style: React.CSSProperties) => React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--wf-ink-3)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      <div style={{ position: "relative" }}>
        <IconCmp
          size={14}
          strokeWidth={1.75}
          color="var(--wf-ink-3)"
          {...({ style: {
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          } } as { style: React.CSSProperties })}
        />
        {children(inputStyle)}
      </div>
    </label>
  );
}

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: session, isLoading } = useQuery({
    queryKey: ["auth", "session"],
    queryFn: () => authApi.getSession(),
    retry: false,
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const signIn = useMutation({
    mutationFn: () => authApi.signInEmail({ email, password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
      const redirectTo = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      navigate(redirectTo ?? "/painel", { replace: true });
    },
  });

  if (isLoading) return null;
  if (session) return <Navigate to="/painel" replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    signIn.mutate();
  }

  return (
    <div
      className="wf"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 24,
        background:
          "radial-gradient(circle at 30% 20%, rgba(255,112,0,0.08), transparent 50%), radial-gradient(circle at 80% 80%, rgba(6,62,110,0.08), transparent 50%), var(--wf-paper)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: "100%", maxWidth: 420 }}
      >
        <Card padding={32}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
            <img
              src="/mohdus-logo.png"
              alt="Mohdus"
              style={{ width: 160 }}
            />
          </div>

          <h1
            className="wf-display"
            style={{
              fontSize: 22,
              textAlign: "center",
              marginBottom: 4,
              marginTop: 0,
              letterSpacing: "-0.015em",
            }}
          >
            Entrar no Mohdus
          </h1>
          <p
            style={{
              fontSize: 13,
              color: "var(--wf-ink-3)",
              textAlign: "center",
              marginBottom: 24,
              marginTop: 0,
            }}
          >
            Use seu e-mail e senha de acesso
          </p>

          <form
            onSubmit={onSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <Field label="E-mail" icon={Mail}>
              {(style) => (
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={style}
                  placeholder="seu@email.com"
                />
              )}
            </Field>

            <Field label="Senha" icon={Lock}>
              {(style) => (
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={style}
                  placeholder="••••••••"
                />
              )}
            </Field>

            {signIn.isError && (
              <Notice tone="danger">{signIn.error.message}</Notice>
            )}

            <button
              type="submit"
              disabled={signIn.isPending}
              className="wf-btn wf-btn--primary"
              style={{
                width: "100%",
                marginTop: 4,
                opacity: signIn.isPending ? 0.6 : 1,
                cursor: signIn.isPending ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 16px",
                fontSize: 14,
              }}
            >
              {signIn.isPending ? "Entrando…" : (
                <>
                  Entrar <ArrowRight size={13} strokeWidth={2} color="#fff" />
                </>
              )}
            </button>
          </form>

          <p
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              textAlign: "center",
              marginTop: 22,
              lineHeight: 1.55,
            }}
          >
            Acesso restrito. Para criar conta, fale com o admin da sua empresa.
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
